/**
 * @file     PropertyHighlights.tsx
 * @purpose  Highlight and amenity pills: a 32px hairline pill per feature,
 *           its ListingIcons glyph (16, secondary ink) before the visible
 *           label, wrapping onto as many rows as they need.
 */
'use client';

import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { hubColors, microLabelSx } from '@/components/hub/hubKitStyles';
import { ListingIcon, highlightIconName, type ListingIconName } from '@/components/icons/ListingIcons';
import { layoutTokens } from '@/design-system/tokens/layout';

export interface FeaturePill {
  label: string;
  /** Defaults to the icon matched on the label text. */
  icon?: ListingIconName;
}

export function FeaturePills({ labels, items, ariaLabel }: { labels?: string[]; items?: FeaturePill[]; ariaLabel?: string }) {
  const all: FeaturePill[] = items ?? (labels ?? []).map((label) => ({ label }));
  const unique = all.filter((pill, i) => pill.label.trim().length > 0 && all.findIndex((p) => p.label === pill.label) === i);
  if (unique.length === 0) return null;
  return (
    <Box component="ul" aria-label={ariaLabel} sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {unique.map((pill) => (
        <Box
          component="li"
          key={pill.label}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
            minHeight: 32,
            pl: 2.5,
            pr: 3,
            borderRadius: `${layoutTokens.radius.pill}px`,
            border: `1px solid ${hubColors.borderSubtle}`,
            backgroundColor: hubColors.surfaceCard,
            color: hubColors.textPrimary,
            fontSize: 14,
            lineHeight: 1.3,
          }}
        >
          <Box component="span" sx={{ display: 'inline-flex', color: hubColors.textSecondary }}>
            <ListingIcon name={pill.icon ?? highlightIconName(pill.label)} size={16} />
          </Box>
          {pill.label}
        </Box>
      ))}
    </Box>
  );
}

interface Props {
  features: string[];
  /** Adds a micro label above the pills for standalone use outside a titled section. */
  standalone?: boolean;
}

export function PropertyHighlights({ features, standalone = false }: Props) {
  const { t } = useTranslation();
  if (features.length === 0) return null;
  return (
    <Box sx={standalone ? { mt: 4 } : undefined}>
      {standalone ? (
        <Box component="p" sx={{ ...microLabelSx, mb: 2 }}>
          {t('listingDetail.facts.highlights', { defaultValue: 'Highlights' })}
        </Box>
      ) : null}
      <FeaturePills labels={features} />
    </Box>
  );
}
